/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { InfoCardWithExternalLink, linkOption } from 'src/core/components/InfoCardWithExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import {
    codeSamplesCsharpLink,
    codeSamplesJavaLink,
    codeSamplesJavaScriptLink,
    codeSamplesPythonLink,
    languageDocsLink
} from 'src/languageStudio/appShell/landingPage/utils/landingPageLinks';

const messages = defineMessages({
    title: {
        id: 'languageStudio.learningSection.title',
        defaultMessage: 'Learning resources'
    },
    readDocs: {
        id: 'languageStudio.learningSection.readDocs',
        defaultMessage: 'Read the documentation'
    },
    readDocsDescription: {
        id: 'languageStudio.learningSection.readDocsDescription',
        defaultMessage: `Interpret natural language, classify documents,
         get real-time translations, or integrate language into your bot experiences.`
    },
    readDocsLink: {
        id: 'languageStudio.learningSection.readDocsLink',
        defaultMessage: 'Language documentation'
    },
    exploreCodeSamples: {
        id: 'languageStudio.learningSection.exploreCodeSamples',
        defaultMessage: 'Explore our code samples'
    },
    exploreCodeSamplesDescription: {
        id: 'languageStudio.learningSection.exploreCodeSamplesDescription',
        defaultMessage: 'Explore our samples and discover the things you can build with Language services.'
    },
    exploreCodeSamplesLink: {
        id: 'languageStudio.learningSection.exploreCodeSamplesLink',
        defaultMessage: 'Code samples'
    },
    watchVideo: {
        id: 'languageStudio.learningSection.watchVideo',
        defaultMessage: 'Watch a video (coming soon)'
    },
    watchVideoDescription: {
        id: 'languageStudio.learningSection.watchVideoDescription',
        defaultMessage: 'Understand language and infuse intelligence in your apps with Language APIs.'
    },
    watchVideoLink: {
        id: 'languageStudio.learningSection.watchVideoLink',
        defaultMessage: 'Video tutorials'
    },
    msLearn: {
        id: 'languageStudio.learningSection.msLearn',
        defaultMessage: 'Microsoft Learn (coming soon)'
    },
    msLearnDescription: {
        id: 'languageStudio.learningSection.msLearnDescription',
        defaultMessage: `Discover new skills, find certifications,
        and advance your career in minutes with interactive, hands-on learning paths.`
    },
    msLearnLink: {
        id: 'languageStudio.learningSection.msLearnLink',
        defaultMessage: 'Microsoft Learn'
    }
});

/**
 * Displays the learning section in the language studio landing page.
 * @todo [LanguageStudio] Add the missing links
 */
export const LearningSection = () => {
    const { localize } = useLocalization();

    const codeSamplesLinkOptions: linkOption[] = [
        {
            key: 'codeSamplesCsharp',
            href: codeSamplesCsharpLink,
            text: `${localize(messages.exploreCodeSamplesLink)} - C#`
        },
        {
            key: 'codeSamplesPython',
            href: codeSamplesPythonLink,
            text: `${localize(messages.exploreCodeSamplesLink)} - Python`
        },
        {
            key: 'codeSamplesJava',
            href: codeSamplesJavaLink,
            text: `${localize(messages.exploreCodeSamplesLink)} - Java`
        },
        {
            key: 'codeSamplesJavaScript',
            href: codeSamplesJavaScriptLink,
            text: `${localize(messages.exploreCodeSamplesLink)} - Javascript`
        }
    ];

    return (
        <Stack tokens={{ childrenGap: 20, padding: '0 0 3rem 0' }}>
            <LuisText variant="large" fontWeight={500}>
                {localize(messages.title)}
            </LuisText>

            <Stack wrap horizontal tokens={{ childrenGap: 20 }}>
                <InfoCardWithExternalLink
                    pageName="LandingPage"
                    featureName="landing page"
                    linkKey="LanguageDocsLink"
                    linkHref={languageDocsLink}
                    cardTitle={localize(messages.readDocs)}
                    linkText={localize(messages.readDocsLink)}
                    cardDescription={localize(messages.readDocsDescription)}
                />

                <InfoCardWithExternalLink
                    pageName="LandingPage"
                    featureName="landing page"
                    linkOptions={codeSamplesLinkOptions}
                    cardTitle={localize(messages.exploreCodeSamples)}
                    optionsDropDownTitle={localize(messages.exploreCodeSamplesLink)}
                    cardDescription={localize(messages.exploreCodeSamplesDescription)}
                />

                <InfoCardWithExternalLink
                    linkHref={''}
                    isLinkDisabled
                    pageName="LandingPage"
                    linkKey="WatchVideoLink"
                    featureName="landing page"
                    cardTitle={localize(messages.watchVideo)}
                    linkText={localize(messages.watchVideoLink)}
                    cardDescription={localize(messages.watchVideoDescription)}
                />

                <InfoCardWithExternalLink
                    linkHref={''}
                    isLinkDisabled
                    linkKey="MSLearn"
                    pageName="LandingPage"
                    featureName="landing page"
                    cardTitle={localize(messages.msLearn)}
                    linkText={localize(messages.msLearnLink)}
                    cardDescription={localize(messages.msLearnDescription)}
                />
            </Stack>
        </Stack>
    );
};
